import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  ChevronLeft, Send, Users, Radio, MessageCircle, Tv,
} from 'lucide-react';
import { XbotCoverOverlay } from '../components/XbotCoverOverlay';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GameHubWatch = () => {
  const { slug, sessionId } = useParams();
  const { token, user } = useAuth();
  const [session, setSession] = useState(null);
  const [program, setProgram] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);
  const pollRef = useRef(null);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchSession = useCallback(async () => {
    try {
      const progRes = await fetch(`${API_URL}/api/programs/by-slug/${slug}`);
      if (!progRes.ok) return;
      const prog = await progRes.json();
      setProgram(prog);
      const sesRes = await fetch(`${API_URL}/api/programs/${prog.id}/game-hub/${sessionId}`);
      if (sesRes.ok) setSession(await sesRes.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [slug, sessionId]);

  const fetchChat = useCallback(async () => {
    if (!program) return;
    try {
      const res = await fetch(`${API_URL}/api/programs/${program.id}/game-hub/${sessionId}/chat?limit=200`);
      if (res.ok) setMessages(await res.json());
    } catch { /* ignore */ }
  }, [program, sessionId]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  useEffect(() => {
    if (!program) return;
    fetchChat();
    pollRef.current = setInterval(fetchChat, 3000);
    return () => clearInterval(pollRef.current);
  }, [program, fetchChat]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!newMsg.trim() || !program || sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/programs/${program.id}/game-hub/${sessionId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ content: newMsg.trim() }),
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages(prev => [...prev, msg]);
        setNewMsg('');
      } else { toast.error('Failed to send'); }
    } catch { toast.error('Network error'); }
    setSending(false);
  };

  const color = program?.primary_color || '#FF453A';

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: `${color} transparent ${color} ${color}` }} />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center">
        <Tv className="w-12 h-12 text-zinc-700 mb-3" />
        <p className="text-zinc-500">Watch session not found</p>
        <Link to={`/teams/${slug}`} className="text-cyan-400 hover:underline text-sm mt-2">Back to program</Link>
      </div>
    );
  }

  const renderVideo = () => {
    if (!session.video_url) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center bg-zinc-900 rounded-lg">
          <Radio className="w-16 h-16 text-zinc-700 mb-3 animate-pulse" />
          <p className="text-zinc-500 text-lg">Waiting for stream to start...</p>
          <p className="text-zinc-600 text-sm mt-1">The broadcast will appear here when it goes live</p>
        </div>
      );
    }
    const url = session.video_url;
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const ytId = url.includes('youtu.be')
        ? url.split('/').pop()
        : new URLSearchParams(new URL(url).search).get('v');
      return (
        <iframe src={`https://www.youtube.com/embed/${ytId}?autoplay=1`} className="w-full h-full rounded-lg" allow="autoplay; encrypted-media; fullscreen" allowFullScreen title={session.title} data-testid="video-player" />
      );
    }
    if (url.includes('cloudflarestream.com') || url.includes('videodelivery.net')) {
      const cfId = url.split('/').pop()?.replace('.mp4', '');
      return (
        <iframe src={`https://customer-${cfId}.cloudflarestream.com/iframe`} className="w-full h-full rounded-lg" allow="autoplay; encrypted-media; fullscreen" allowFullScreen title={session.title} data-testid="video-player" />
      );
    }
    return (
      <video src={url} controls autoPlay className="w-full h-full rounded-lg bg-black" data-testid="video-player">
        Your browser does not support the video tag.
      </video>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="game-hub-watch-page">
      {/* Header */}
      <div className="border-b border-zinc-800 px-4 py-3 flex items-center gap-3">
        <Link to={`/teams/${slug}`} className="text-zinc-400 hover:text-white transition-colors" data-testid="back-to-program">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="text-white font-bold text-sm truncate" data-testid="session-title">{session.title}</h1>
          <p className="text-zinc-500 text-xs truncate">{program?.name} {session.team_name ? `- ${session.team_name}` : ''}</p>
        </div>
        <Badge
          className={`text-xs px-2 py-0.5 shrink-0 ${
            session.status === 'live' ? 'bg-red-500/20 text-red-400 border-red-700'
            : session.status === 'ended' ? 'bg-zinc-500/20 text-zinc-400 border-zinc-700'
            : 'bg-amber-500/20 text-amber-400 border-amber-700'
          }`}
          data-testid="session-status"
        >
          {session.status === 'live' && <Radio className="w-3 h-3 mr-1 animate-pulse" />}
          {session.status.toUpperCase()}
        </Badge>
      </div>

      {/* Video + Chat */}
      <div className="flex flex-col lg:flex-row h-[calc(100vh-57px)]">
        {/* Video */}
        <div className="flex-1 p-2 lg:p-4" data-testid="video-area">
          <div className="w-full h-[40vh] lg:h-full relative">{renderVideo()}<XbotCoverOverlay /></div>
        </div>

        {/* Chat */}
        <div className="w-full lg:w-[380px] border-t lg:border-t-0 lg:border-l border-zinc-800 flex flex-col h-[50vh] lg:h-full" data-testid="chat-panel">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4" style={{ color }} />
              <span className="text-white font-semibold text-sm">Game Chat</span>
            </div>
            <div className="flex items-center gap-1 text-zinc-500 text-xs">
              <Users className="w-3 h-3" />
              <span data-testid="message-count">{messages.length} messages</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2" data-testid="chat-messages">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <MessageCircle className="w-8 h-8 text-zinc-700 mb-2" />
                <p className="text-zinc-500 text-sm">No messages yet</p>
                <p className="text-zinc-600 text-xs">Be the first to chat!</p>
              </div>
            ) : (
              messages.map(msg => {
                const isMe = msg.sender_id === user?.id;
                return (
                  <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`} data-testid={`chat-msg-${msg.id}`}>
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={`text-[11px] font-medium ${isMe ? 'text-cyan-400' : 'text-zinc-400'}`}>{msg.sender_name}</span>
                      <span className="text-[10px] text-zinc-600">
                        {new Date(msg.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className={`px-3 py-1.5 rounded-xl text-sm max-w-[85%] ${isMe ? 'bg-zinc-800 text-white rounded-br-sm' : 'bg-zinc-900 text-zinc-200 border border-zinc-800 rounded-bl-sm'}`}>
                      {msg.content}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={chatEndRef} />
          </div>

          {token ? (
            <div className="p-3 border-t border-zinc-800 shrink-0">
              <div className="flex gap-2">
                <Input value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()} placeholder="Send a message..." className="bg-zinc-900 border-zinc-700 text-white text-sm flex-1" data-testid="chat-input" />
                <Button size="sm" onClick={sendMessage} disabled={!newMsg.trim() || sending} className="text-white shrink-0" style={{ backgroundColor: color }} data-testid="chat-send-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-3 border-t border-zinc-800 text-center">
              <Link to="/login" className="text-cyan-400 text-sm hover:underline">Sign in to chat</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameHubWatch;
